import { MenuBar } from "@/components/common/MenuBar";
import { NotificationBanner } from "@/components/common/NotificationBanner";
import { LiveTrackingMap } from "@/components/map/live-tracking-map";
import { ThemedText } from "@/components/themed-text";
import { RecoveryButton } from "@/components/ui/recovery-button";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { locationTrackingService } from "@/lib/services/location-tracking";
import { showToast } from "@/lib/toast";
import { useAuthStore } from "@/stores/use-auth-store";
import { useLocationStore } from "@/stores/use-location-store";
import BottomSheet, { BottomSheetScrollView } from "@gorhom/bottom-sheet";
import Ionicons from "@react-native-vector-icons/ionicons";
import React, { useMemo, useRef, useState } from "react";
import { Platform, StyleSheet, Text, View } from "react-native";
import MapView from "react-native-maps";
import { ActivityIndicator, Switch } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

const isWeb = Platform.OS === "web";
export default function DriverHome() {
  const mapRef = useRef<MapView>(null);
  const { user, setUser, theme } = useAuth();
  const activeBooking = useAuthStore((state) => state.activeBooking);
  const [loading, setLoading] = useState(false);
  const desktop = useDesktopLayout();

  const bottomSheetRef = useRef<BottomSheet>(null);
  const { coordinate } = useLocationStore((state) => state);

  const isTracking = user?.driver?.is_online ?? false;

  // Snap points for the bottom sheet
  const snapPoints = useMemo(() => ["25%"], []);

  const handleToggleOnlineStatus = async (value: boolean) => {
    if (!user?.driver) return;

    if (value && !user.fcm_token && !isWeb) {
      showToast.error("Enable push notifications before going online");
      return;
    }

    setLoading(true);
    try {
      if (value) {
        await locationTrackingService.setOnlineStatus(true);
        setUser({ ...user, driver: { ...user.driver, is_online: true } });
      } else {
        await locationTrackingService.stopTracking();
        locationTrackingService.disconnectDispatch();
        await locationTrackingService.setOnlineStatus(false);
        setUser({ ...user, driver: { ...user.driver, is_online: false } });
      }
    } catch (error: any) {
      console.error("Failed to toggle online status:", error);
      const message =
        error.response?.data?.message || "Failed to toggle online status";
      showToast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.background }]}
      edges={["top"]}
    >
      {/* Full Screen Map */}
      <View style={styles.mapContainer}>
        <LiveTrackingMap
          mapRef={mapRef}
          pickupLocation={coordinate}
          showUserLocation={false}
          style={styles.map}
        />

        {/* Map Overlays */}
        {desktop ? null : <MenuBar style={{ top: 20, left: 20 }} />}
        <NotificationBanner />
        {desktop ? (
        <View
          style={[styles.desktopCard, { backgroundColor: theme.background }]}
        >
          <View style={styles.sheetHeader}>
            <View accessible accessibilityLabel="Welcome back,">
              <ThemedText style={styles.greeting}>Welcome back,</ThemedText>
              <ThemedText style={styles.userName}>
                {user?.first_name} {user?.last_name}
              </ThemedText>
            </View>
            <View style={styles.statusBadge}>
              <View
                style={[
                  styles.statusDot,
                  isTracking ? styles.statusActive : styles.statusInactive,
                ]}
              />
              <Text style={styles.statusText}>
                {isTracking ? "Online" : "Offline"}
              </Text>
            </View>
          </View>
          {activeBooking ? (
            <RecoveryButton />
          ) : (
            <View style={styles.statusCard}>
              <View style={styles.statusInfo}>
                <View
                  style={[
                    styles.statusIconContainer,
                    isTracking
                      ? { backgroundColor: "#DCFCE7" }
                      : { backgroundColor: "#F1F5F9" },
                  ]}
                >
                  <Ionicons
                    name={isTracking ? "radio-outline" : "moon-outline"}
                    size={24}
                    color={
                      isTracking ? BrandColors.success : BrandColors.mediumGray
                    }
                  />
                </View>
                <View>
                  <ThemedText style={styles.statusTitle}>
                    {isTracking ? "You are Online" : "You are Offline"}
                  </ThemedText>
                  <ThemedText style={styles.statusSubtitle}>
                    {isTracking ? "Receiving new jobs" : "Go online to start"}
                  </ThemedText>
                </View>
              </View>
              {loading ? (
                <ActivityIndicator size="small" color={BrandColors.primary} />
              ) : (
                <View>
                  <Switch
                    value={isTracking}
                    onValueChange={handleToggleOnlineStatus}
                    color={BrandColors.success}
                    style={{ transform: [{ scaleX: 1.3 }, { scaleY: 1.3 }] }}
                  />
                </View>
              )}
            </View>
          )}
        </View>
        ) : null}
      </View>

      {desktop ? null : (
        <BottomSheet
          ref={bottomSheetRef}
          index={0}
          snapPoints={snapPoints}
          handleIndicatorStyle={styles.sheetHandle}
          backgroundStyle={[
            styles.sheetBackground,
            { backgroundColor: theme.background },
          ]}
        >
          <BottomSheetScrollView contentContainerStyle={styles.sheetContent}>
            <View style={styles.sheetHeader}>
              <View accessible accessibilityLabel="Welcome back,">
                <ThemedText style={styles.greeting}>Welcome back,</ThemedText>
                <ThemedText style={styles.userName}>
                  {user?.first_name} {user?.last_name}
                </ThemedText>
              </View>
              <View style={styles.statusBadge}>
                <View
                  style={[
                    styles.statusDot,
                    isTracking ? styles.statusActive : styles.statusInactive,
                  ]}
                />
                <Text style={styles.statusText}>
                  {isTracking ? "Online" : "Offline"}
                </Text>
              </View>
            </View>
            {activeBooking ? (
              <RecoveryButton />
            ) : (
              <View style={styles.statusCard}>
                <View style={styles.statusInfo}>
                  <View
                    style={[
                      styles.statusIconContainer,
                      isTracking
                        ? { backgroundColor: "#DCFCE7" }
                        : { backgroundColor: "#F1F5F9" },
                    ]}
                  >
                    <Ionicons
                      name={isTracking ? "radio-outline" : "moon-outline"}
                      size={24}
                      color={
                        isTracking ? BrandColors.success : BrandColors.mediumGray
                      }
                    />
                  </View>
                  <View>
                    <ThemedText style={styles.statusTitle}>
                      {isTracking ? "You are Online" : "You are Offline"}
                    </ThemedText>
                    <ThemedText style={styles.statusSubtitle}>
                      {isTracking ? "Receiving new jobs" : "Go online to start"}
                    </ThemedText>
                  </View>
                </View>
                {loading ? (
                  <ActivityIndicator size="small" color={BrandColors.primary} />
                ) : (
                  <View>
                    <Switch
                      value={isTracking}
                      onValueChange={handleToggleOnlineStatus}
                      color={BrandColors.success}
                      style={{ transform: [{ scaleX: 1.3 }, { scaleY: 1.3 }] }}
                    />
                  </View>
                )}
              </View>
            )}
          </BottomSheetScrollView>
        </BottomSheet>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  mapContainer: {
    flex: 1,
    position: "relative",
  },
  map: {
    width: "100%",
    height: "100%",
  },
  headerOverlay: {
    position: "absolute",
    top: 20,
    left: 20,
    zIndex: 10,
  },
  menuButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: BrandColors.white,
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
  },
  sheetBackground: {
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
  },
  sheetHandle: {
    backgroundColor: "#475569",
    width: 60,
  },
  sheetContent: {
    paddingHorizontal: 20,
    paddingBottom: 40,
  },
  sheetHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
    marginTop: 8,
  },
  greeting: {
    fontSize: 14,
    color: BrandColors.mediumGray,
    fontWeight: "500",
  },
  userName: {
    fontSize: 24,
    letterSpacing: -0.5,
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: BrandColors.white,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    gap: 6,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  statusText: {
    color: BrandColors.charcoal,
    padding: 2,
  },
  statusActive: {
    backgroundColor: BrandColors.success,
  },
  statusInactive: {
    backgroundColor: BrandColors.mediumGray,
  },
  statusCard: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: BrandColors.white,
    padding: 16,
    borderRadius: 20,
    marginBottom: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.03,
    shadowRadius: 8,
    elevation: 2,
  },
  statusInfo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  statusIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
  },
  statusTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: BrandColors.charcoal,
  },
  desktopCard: {
    position: "absolute",
    left: 24,
    bottom: 24,
    width: 420,
    maxWidth: "92%",
    borderRadius: 20,
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 8,
    zIndex: 2,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 6,
  },
  statusSubtitle: {
    fontSize: 12,
    color: BrandColors.mediumGray,
    marginTop: 2,
  },
});
